import React from 'react';
import PropTypes from 'prop-types';
import { groupBy, isEqual } from 'lodash';
import { bbox } from '@turf/bbox';
import { featureCollection, lineString } from '@turf/helpers';

import maplibre from '../../lib/maplibre';
import MapContext from './MapContext';
import MapControl from './MapControl';
import HereTrafficLayer from './layers/HereTrafficLayer';
import { pointOnCircle, toLngLat } from '../../lib/map_helpers';

const markersIgnoredFromClustering = ['station', 'vehicleBreadcrumb'];
const helperLinesSourceId = 'cluster-helper-lines';
// MapLibre zoom levels are based on 512px tiles, one level below the levels of the 256px HERE tiles
const detailZoom = 13;
const maxFitZoom = 17;

const lineCollection = (lines) => featureCollection(lines.map((coordinates) => lineString(coordinates)));

export default class HereMap extends React.Component {
  constructor(props) {
    super(props);

    this.containerRef = React.createRef();
    this.mapItems = new Set();
    this.displacedMapItems = new Set();
    this.helperLines = [];
    this.disableAutomaticMapHandling = this.disableAutomaticMapHandling.bind(this);
    this.enableAutomaticMapHandling = this.enableAutomaticMapHandling.bind(this);
    this.unclusterify = this.unclusterify.bind(this);

    this.state = {
      mapContext: null,
      automaticMapHandling: props.autoZoom
    };
  }

  componentDidMount() {
    this.map = new maplibre.Map({
      container: this.containerRef.current,
      style: this.baseStyle(),
      ...this.initialView(),
      dragRotate: false,
      touchPitch: false,
      scrollZoom: !this.state.automaticMapHandling,
      attributionControl: { customAttribution: `&copy; ${new Date().getFullYear()} HERE` }
    });
    this.map.touchZoomRotate.disableRotation();
    this.map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'top-left');

    this.map
      .getContainer()
      .querySelectorAll('.maplibregl-ctrl-zoom-in, .maplibregl-ctrl-zoom-out')
      .forEach((control) => control.addEventListener('click', this.disableAutomaticMapHandling));

    this.map.on('mousedown', this.disableAutomaticMapHandling);
    this.map.on('touchstart', this.disableAutomaticMapHandling);
    this.map.on('zoomend', this.unclusterify);
    this.map.once('load', () => {
      this.map.addSource(helperLinesSourceId, { type: 'geojson', data: lineCollection([]) });
      this.map.addLayer({
        id: helperLinesSourceId,
        type: 'line',
        source: helperLinesSourceId,
        paint: { 'line-color': 'black', 'line-width': 1, 'line-dasharray': [2, 2] }
      });

      this.setState({
        mapContext: {
          map: this.map,
          register: (item) => this.mapItems.add(item),
          unregister: (item) => {
            this.mapItems.delete(item);
            this.displacedMapItems.delete(item);
          },
          isRemoved: () => this.isRemoved
        }
      });
    });
  }

  componentDidUpdate(_prevProps, prevState) {
    if (!this.state.mapContext) return;

    if (prevState.automaticMapHandling !== this.state.automaticMapHandling) {
      if (this.state.automaticMapHandling) {
        this.map.scrollZoom.disable();
      } else {
        this.map.scrollZoom.enable();
      }
    }

    this.unclusterify();

    if (this.props.autoZoom && this.state.automaticMapHandling) {
      this.fitToMapItems();
    }
  }

  componentWillUnmount() {
    this.isRemoved = true;
    this.map.remove();
  }

  baseStyle() {
    const hereApiKey = import.meta.env.VITE_HERE_API_KEY_STELLWERK_MAPS;

    return {
      version: 8,
      sources: {
        'here-base': {
          type: 'raster',
          // 512px tiles with doubled label size shown in 256px slots keep the map sharp on high density displays
          tiles: [`https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?size=512&ppi=200&apiKey=${hereApiKey}`],
          tileSize: 256,
          maxzoom: 20
        }
      },
      layers: [{ id: 'here-base', type: 'raster', source: 'here-base' }]
    };
  }

  initialView() {
    const { boundingBox, center } = this.props;

    if (boundingBox) {
      const min = toLngLat(boundingBox.min);
      const max = toLngLat(boundingBox.max);

      // MapLibre cannot fit a bounding box without an extent
      if (!isEqual(min, max)) return { bounds: [min, max] };

      return { center: min, zoom: detailZoom };
    }

    if (center) return { center: toLngLat(center), zoom: detailZoom };

    return { center: [8, 50], zoom: 7 };
  }

  fitToMapItems() {
    const features = [...this.mapItems].flatMap((item) => item.toFeatures());
    if (features.length === 0) return;

    const [west, south, east, north] = bbox(featureCollection(features));
    this.map.fitBounds(
      [[west, south], [east, north]],
      { animate: this.props.animated, maxZoom: maxFitZoom, padding: 20 }
    );
  }

  unclusterify() {
    if (!this.state.mapContext) return;

    const clusterableItems = [...this.mapItems].filter(
      (item) => item.getLngLat && !markersIgnoredFromClustering.includes(item.type)
    );
    const clusters = Object.values(groupBy(clusterableItems, (item) => item.getLngLat().join(','))).filter(
      (items) => items.length > 1
    );
    const displacedMapItems = new Set();
    const helperLines = [];

    clusters.forEach((items) => {
      const position = items[0].getLngLat();
      const markerPoint = this.map.project(position);
      const radius = 50 - (this.map.getZoom() + 1);

      items.forEach((item, index) => {
        const newPoint = pointOnCircle(markerPoint.x, markerPoint.y, radius, items.length, index);
        const newLngLat = this.map.unproject(newPoint).toArray();

        item.displayAt(newLngLat);
        displacedMapItems.add(item);
        helperLines.push([position, newLngLat]);
      });
    });

    this.displacedMapItems.forEach((item) => {
      if (!displacedMapItems.has(item)) item.displayAt(null);
    });
    this.displacedMapItems = displacedMapItems;

    if (!isEqual(helperLines, this.helperLines)) {
      this.helperLines = helperLines;
      this.map.getSource(helperLinesSourceId).setData(lineCollection(helperLines));
    }
    if (helperLines.length > 0) this.map.moveLayer(helperLinesSourceId);
  }

  disableAutomaticMapHandling() {
    if (this.state.automaticMapHandling) this.setState({ automaticMapHandling: false });
  }

  enableAutomaticMapHandling() {
    this.setState({ automaticMapHandling: true });
  }

  renderResetAutoZoomButton() {
    if (!this.props.autoZoom) {
      return null;
    }
    if (this.state.automaticMapHandling) {
      return null;
    }

    return (
      <MapControl position="top-right">
        <button type="button" className="btn btn-light" onClick={this.enableAutomaticMapHandling}>
          {window.locales.HereMap.resetAutoZoom}
        </button>
      </MapControl>
    );
  }

  renderHereTrafficLayer() {
    if (this.props.noTraffic) {
      return null;
    }

    return <HereTrafficLayer />;
  }

  renderMapContent() {
    if (!this.state.mapContext) return null;

    return (
      <MapContext.Provider value={this.state.mapContext}>
        {this.renderHereTrafficLayer()}
        {this.props.children}
        {this.renderResetAutoZoomButton()}
      </MapContext.Provider>
    );
  }

  render() {
    return (
      <div ref={this.containerRef} className="here-map">
        {this.renderMapContent()}
      </div>
    );
  }
}

HereMap.propTypes = {
  autoZoom: PropTypes.bool,
  noTraffic: PropTypes.bool,
  children: PropTypes.node,
  boundingBox: PropTypes.shape({
    min: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
    max: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number })
  }),
  center: PropTypes.object,
  animated: PropTypes.bool
};

HereMap.defaultProps = {
  autoZoom: false,
  noTraffic: false,
  animated: true
};
